import React, { useEffect, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { SKILL_CATEGORIES } from '../../../services/skillService';

const PlatformSelector = ({
    value,
    onChange,
    label = '',
    disabled = false,
    size = 'default',
    direction = 'down', // Add direction support if missing or ensure it's used
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const platforms = Object.entries(SKILL_CATEGORIES).map(([id, category]) => ({
        id: id,
        name: category.name,
        icon: category.icon,
    }));

    const selectedPlatform = platforms.find(p => p.id === value) || platforms[0];

    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (e) => {
            if (!e.target.closest('.platform-selector')) setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const handleSelect = (platformId) => {
        onChange(platformId);
        setIsOpen(false);
    };

    const sizeClasses = {
        small: 'h-8 text-sm px-3',
        default: 'h-10 text-base px-4',
        large: 'h-12 text-lg px-5',
    };

    const PLATFORM_COLORS = {
        xiaohongshu: {
            primary: '#FF2442',
            bg: 'rgba(255, 36, 66, 0.05)',
            border: 'rgba(255, 36, 66, 0.1)',
        },
        wechat: {
            primary: '#07C160',
            bg: 'rgba(7, 193, 96, 0.05)',
            border: 'rgba(7, 193, 96, 0.1)',
        }
    };

    const currentTheme = PLATFORM_COLORS[value] || PLATFORM_COLORS.xiaohongshu;

    return (
        <div className={`platform-selector relative ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    borderColor: isOpen ? currentTheme.primary : 'transparent',
                    backgroundColor: isOpen ? '#fff' : '#F7F8FA'
                }}
                className={`
                    flex items-center justify-between gap-2
                    ${sizeClasses[size]} rounded-2xl
                    border transition-all duration-300
                    w-full min-w-[124px] group/btn
                `}
            >
                <div className="flex items-center gap-1.5 min-w-0">
                    {label && <span className="text-gray-400 whitespace-nowrap font-medium">{label}</span>}
                    <span
                        className="font-bold truncate transition-colors duration-300"
                        style={{ color: currentTheme.primary }}
                    >
                        {selectedPlatform.name}
                    </span>
                </div>
                <ChevronDown
                    className={`w-3.5 h-3.5 transition-all duration-300 ${isOpen ? 'rotate-180' : 'text-gray-400 group-hover/btn:text-gray-600'}`}
                    style={{ color: isOpen ? currentTheme.primary : undefined }}
                />
            </button>

            {isOpen && (
                <div className={`absolute ${direction === 'up' ? 'bottom-full mb-2' : 'top-full mt-2'} left-0 right-0 z-[60] bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-100 shadow-2xl animate-in fade-in slide-in-from-${direction === 'up' ? 'bottom' : 'top'}-2 duration-300 overflow-hidden min-w-[140px]`}>
                    <div className="p-1.5">
                        {platforms.filter(p => PLATFORM_COLORS[p.id]).map((platform) => (
                            <button
                                key={platform.id}
                                onClick={() => handleSelect(platform.id)}
                                style={{
                                    backgroundColor: value === platform.id ? PLATFORM_COLORS[platform.id].bg : 'transparent',
                                    color: value === platform.id ? PLATFORM_COLORS[platform.id].primary : '#374151'
                                }}
                                className={`
                                    w-full text-left p-2.5 rounded-xl
                                    hover:bg-gray-50 transition-all duration-200
                                    flex items-center justify-between gap-3
                                    group/item
                                `}
                            >
                                <div className="flex items-center gap-2.5 min-w-0">
                                    <span className="text-lg group-hover/item:scale-110 transition-transform">{platform.icon}</span>
                                    <span className="font-semibold truncate">{platform.name}</span>
                                </div>
                                {value === platform.id && (
                                    <Check className="w-4 h-4 flex-shrink-0" style={{ color: PLATFORM_COLORS[platform.id].primary }} />
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PlatformSelector;
